/**
 * Componente: OverviewTab
 * Descrição: Aba de Visão Geral da tela de Gerenciamento do Tenant
 * 
 * Exibe:
 * - Cards de KPIs principais (plano, mensagens, agentes, inboxes)
 * - Gráfico de mensagens dos últimos 7 dias
 * - Barras de progresso de uso de recursos
 * - Lista de add-ons ativos
 * 
 * @author Sistema
 * @update 2026-01-19 - Criação inicial
 */

import { 
  Crown, 
  Sparkles, 
  Zap, 
  Package, 
  MessageSquare, 
  Users, 
  Inbox, 
  Calendar,
  TrendingUp,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { TenantManagementData } from "@/hooks/useTenantManagement";

// ============================================================================
// INTERFACES
// ============================================================================

interface OverviewTabProps {
  data: TenantManagementData | null;
  messagesByDay?: Array<{ date: string; sent: number; received: number }>;
  isLoading?: boolean;
}

// ============================================================================
// COMPONENTES AUXILIARES
// ============================================================================

/**
 * Card de KPI com design premium
 */
const KpiCard = ({
  title,
  value,
  description,
  icon: Icon,
  variant = "default",
  badge,
  isLoading,
}: {
  title: string;
  value: string | number;
  description?: string;
  icon: React.ElementType;
  variant?: "primary" | "success" | "warning" | "info" | "default";
  badge?: { label: string; variant: "success" | "warning" | "destructive" };
  isLoading?: boolean;
}) => {
  // Mapear variantes para cores usando tokens do design system
  const variantStyles = {
    primary: {
      iconBg: "bg-primary/10",
      iconColor: "text-primary",
    },
    success: {
      iconBg: "bg-emerald-500/10",
      iconColor: "text-emerald-600 dark:text-emerald-400",
    },
    warning: {
      iconBg: "bg-amber-500/10",
      iconColor: "text-amber-600 dark:text-amber-400",
    },
    info: {
      iconBg: "bg-blue-500/10",
      iconColor: "text-blue-600 dark:text-blue-400",
    },
    default: {
      iconBg: "bg-muted",
      iconColor: "text-muted-foreground",
    },
  };

  const styles = variantStyles[variant];

  if (isLoading) {
    return (
      <Card className="relative overflow-hidden">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <Skeleton className="h-12 w-12 rounded-xl" />
            <div className="flex-1">
              <Skeleton className="h-4 w-24 mb-2" />
              <Skeleton className="h-8 w-16" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="relative overflow-hidden transition-all duration-300 hover:shadow-lg hover:translate-y-[-2px]">
      {/* Borda gradient no topo */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
      
      <CardContent className="p-6">
        <div className="flex items-center gap-4">
          <div className={cn(
            "flex h-12 w-12 items-center justify-center rounded-xl flex-shrink-0",
            styles.iconBg
          )}>
            <Icon className={cn("h-6 w-6", styles.iconColor)} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-muted-foreground truncate">
                {title}
              </p>
              {badge && (
                <Badge 
                  variant={badge.variant === "warning" ? "secondary" : badge.variant === "success" ? "secondary" : badge.variant}
                  className={cn(
                    "text-xs px-2 py-0 h-5",
                    badge.variant === "success" && "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-0",
                    badge.variant === "warning" && "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-0"
                  )}
                >
                  {badge.label}
                </Badge>
              )}
            </div>
            <p className="text-2xl font-bold tracking-tight mt-1">
              {value}
            </p>
            {description && (
              <p className="text-xs text-muted-foreground mt-1">
                {description}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * Barra de progresso de uso de recurso
 */
const ResourceUsageBar = ({
  label,
  current,
  max,
  icon: Icon,
  isUnlimited = false,
  isLoading,
}: {
  label: string;
  current: number;
  max: number;
  icon: React.ElementType;
  isUnlimited?: boolean;
  isLoading?: boolean;
}) => {
  const percentage = isUnlimited ? 0 : (max > 0 ? Math.min((current / max) * 100, 100) : 0);
  const isNearLimit = percentage >= 80;
  const isAtLimit = percentage >= 100;

  if (isLoading) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-16" />
        </div>
        <Skeleton className="h-2 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{label}</span>
        </div>
        <span className={cn(
          "font-mono",
          isAtLimit && "text-destructive",
          isNearLimit && !isAtLimit && "text-amber-600 dark:text-amber-400"
        )}>
          {current} / {isUnlimited ? "∞" : max}
        </span>
      </div>
      {!isUnlimited && (
        <Progress 
          value={percentage} 
          className={cn(
            "h-2",
            isAtLimit && "[&>div]:bg-destructive",
            isNearLimit && !isAtLimit && "[&>div]:bg-amber-500"
          )}
        />
      )}
      {isUnlimited && (
        <div className="h-2 rounded-full bg-muted relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-primary/40 to-primary/20 animate-pulse" />
        </div>
      )}
    </div>
  );
};

/**
 * Mini gráfico de barras para mensagens
 */
const MessagesBarChart = ({
  data,
  isLoading,
}: {
  data?: Array<{ date: string; sent: number; received: number }>;
  isLoading?: boolean;
}) => {
  if (isLoading) {
    return (
      <div className="h-32 flex items-end gap-1">
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} className="flex-1 space-y-1">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ))}
      </div>
    );
  }

  // Estado vazio quando não há dados de mensagens
  if (!data || data.length === 0) {
    return (
      <div className="h-32 flex items-center justify-center text-muted-foreground text-sm">
        <MessageSquare className="h-5 w-5 mr-2 opacity-50" />
        Sem dados de mensagens neste período
      </div>
    );
  }

  // Encontrar valor máximo para escala
  const maxValue = Math.max(
    ...data.map(d => Math.max(d.sent, d.received)),
    1 // Evitar divisão por zero
  );

  return (
    <div className="space-y-3">
      <div className="h-32 flex items-end gap-1">
        {data.map((item, index) => {
          const sentHeight = (item.sent / maxValue) * 100;
          const receivedHeight = (item.received / maxValue) * 100;
          
          return (
            <div key={index} className="flex-1 flex gap-0.5 items-end h-full">
              {/* Barra de enviadas */}
              <div 
                className="flex-1 bg-primary/80 rounded-t-sm transition-all duration-300 hover:bg-primary min-h-[2px]"
                style={{ height: `${Math.max(sentHeight, 2)}%` }}
                title={`Enviadas: ${item.sent}`}
              />
              {/* Barra de recebidas */}
              <div 
                className="flex-1 bg-emerald-500/80 rounded-t-sm transition-all duration-300 hover:bg-emerald-500 min-h-[2px]"
                style={{ height: `${Math.max(receivedHeight, 2)}%` }}
                title={`Recebidas: ${item.received}`}
              />
            </div>
          );
        })}
      </div>
      
      {/* Labels de data */}
      <div className="flex gap-1">
        {data.map((item, index) => (
          <div key={index} className="flex-1 text-center">
            <span className="text-[10px] text-muted-foreground">
              {format(new Date(item.date), "dd/MM", { locale: ptBR })}
            </span>
          </div>
        ))}
      </div>

      {/* Legenda */}
      <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-primary/80" />
          <span>Enviadas</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-emerald-500/80" />
          <span>Recebidas</span>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export function OverviewTab({ data, messagesByDay, isLoading }: OverviewTabProps) {
  // Se data é null, exibir skeleton ou loading state
  if (!data) {
    return (
      <div className="space-y-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="relative overflow-hidden">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <Skeleton className="h-12 w-12 rounded-xl" />
                  <div className="flex-1">
                    <Skeleton className="h-4 w-24 mb-2" />
                    <Skeleton className="h-8 w-16" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // Ícone do plano
  const getPlanIcon = (planId: string) => {
    switch (planId) {
      case "enterprise": return Crown;
      case "professional": return Sparkles;
      case "starter": return Zap;
      default: return Package;
    }
  };

  // Status do plano para badge
  const getStatusBadge = () => {
    if (data.billing.isInTrial) {
      return { label: `Trial • ${data.billing.trialDaysRemaining}d`, variant: "warning" as const };
    }
    if (data.billing.subscriptionStatus === "active") {
      return { label: "Ativo", variant: "success" as const };
    }
    if (data.billing.subscriptionStatus === "past_due") {
      return { label: "Pagamento Pendente", variant: "destructive" as const };
    }
    return undefined;
  };

  // Formatar próxima cobrança
  const nextBillingDate = data.billing.currentPeriodEnd
    ? format(new Date(data.billing.currentPeriodEnd), "dd 'de' MMMM", { locale: ptBR })
    : "-";

  // Calcular totais do período (7 dias) a partir do messagesByDay
  const periodTotals = (messagesByDay || []).reduce(
    (acc, day) => ({
      sent: acc.sent + (day.sent || 0),
      received: acc.received + (day.received || 0),
    }),
    { sent: 0, received: 0 }
  );
  const periodTotal = periodTotals.sent + periodTotals.received;

  return (
    <div className="space-y-8">
      {/* Seção 1: KPIs Principais */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          title="Plano Atual"
          value={data.billing.planName}
          description={`Próx. cobrança: ${nextBillingDate}`}
          icon={getPlanIcon(data.billing.plan)}
          variant="primary"
          badge={getStatusBadge()}
          isLoading={isLoading}
        />
        <KpiCard
          title="Mensagens (7 dias)"
          value={periodTotal.toLocaleString("pt-BR")}
          description={`↑ ${periodTotals.sent.toLocaleString("pt-BR")} env. | ↓ ${periodTotals.received.toLocaleString("pt-BR")} rec.`}
          icon={MessageSquare}
          variant="success"
          isLoading={isLoading}
        />
        <KpiCard
          title="Agentes"
          value={`${data.usage.currentAgents} / ${data.limits.maxAgents}`}
          description={`${Math.max(0, data.limits.maxAgents - data.usage.currentAgents)} disponíveis`}
          icon={Users}
          variant="info"
          isLoading={isLoading}
        />
        <KpiCard
          title="Inboxes"
          value={`${data.usage.currentInboxes} / ${data.limits.maxInboxes}`}
          description={`${Math.max(0, data.limits.maxInboxes - data.usage.currentInboxes)} disponíveis`}
          icon={Inbox}
          variant="warning"
          isLoading={isLoading}
        />
      </div>

      {/* Seção 2: Gráfico de Mensagens + Uso de Recursos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gráfico de Mensagens - Últimos 7 dias */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base font-semibold">Mensagens (7 dias)</CardTitle>
                <CardDescription>Volume de mensagens enviadas e recebidas</CardDescription>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold">{periodTotal.toLocaleString("pt-BR")}</p>
                <p className="text-xs text-muted-foreground">
                  ↑ {periodTotals.sent.toLocaleString("pt-BR")} | ↓ {periodTotals.received.toLocaleString("pt-BR")}
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <MessagesBarChart data={messagesByDay} isLoading={isLoading} />
          </CardContent>
        </Card>

        {/* Uso de Recursos */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base font-semibold">Uso de Recursos</CardTitle>
                <CardDescription>Consumo atual vs limites do plano</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            <ResourceUsageBar
              label="Agentes"
              current={data.usage.currentAgents}
              max={data.limits.maxAgents}
              icon={Users}
              isUnlimited={false}
              isLoading={isLoading}
            />
            <ResourceUsageBar
              label="Inboxes"
              current={data.usage.currentInboxes}
              max={data.limits.maxInboxes}
              icon={Inbox}
              isUnlimited={false}
              isLoading={isLoading}
            />
            {/* Mensagens: -1 significa realmente ilimitado */}
            {data.limits.maxMessages !== 0 && (
              <ResourceUsageBar
                label="Mensagens/mês"
                current={data.messages.total}
                max={data.limits.maxMessages < 0 ? 0 : data.limits.maxMessages}
                icon={MessageSquare}
                isUnlimited={data.limits.maxMessages < 0}
                isLoading={isLoading}
              />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Seção 3: Add-ons Ativos (se houver) */}
      {data.activeAddons.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold">Add-ons Ativos</CardTitle>
            <CardDescription>Recursos adicionais contratados</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {data.activeAddons.map((addon) => (
                <Badge 
                  key={addon.id} 
                  variant="secondary"
                  className="px-3 py-1.5 text-sm"
                >
                  {addon.name}
                  {addon.quantity > 1 && (
                    <span className="ml-1.5 text-xs text-muted-foreground">
                      x{addon.quantity}
                    </span>
                  )}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Seção 4: Resumo de Conversas */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold">Resumo de Conversas</CardTitle>
          <CardDescription>Status atual das conversas no workspace</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="text-center p-4 rounded-lg bg-muted/50">
              <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                {data.conversations.pending}
              </p>
              <p className="text-sm text-muted-foreground mt-1">Pendentes</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-muted/50">
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {data.conversations.open}
              </p>
              <p className="text-sm text-muted-foreground mt-1">Abertas</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-muted/50">
              <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                {data.conversations.closed}
              </p>
              <p className="text-sm text-muted-foreground mt-1">Fechadas</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-muted/50">
              <p className="text-2xl font-bold">
                {data.conversations.total}
              </p>
              <p className="text-sm text-muted-foreground mt-1">Total</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
